import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import "moment/locale/pt-br";
import {
  TbArrowLeft,
  TbArrowsLeftRight,
  TbCheck,
  TbChevronDown,
  TbCopy,
  TbDownload,
  TbPrinter,
  TbShare2,
  TbX,
} from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import { formatCurrency } from "../../../Utils/formatters";
import { useReceiptDownload } from "../ReceiptModal/useReceiptDownload";
import { useReceiptPrint } from "../ReceiptModal/useReceiptPrint";
import { useToast } from "../../../Hooks/toast";

moment.locale("pt-br");

const findDetailValue = (details, sectionName, fieldName) => {
  const section = details.find(
    (item) => item.value?.toLowerCase() === sectionName.toLowerCase()
  );
  const row = section?.items?.find(
    (item) => item.title?.toLowerCase() === fieldName.toLowerCase()
  );
  return row?.value || "";
};

const formatAuthValue = (value) => {
  if (!value || value.length <= 24) return value;
  return `${value.slice(0, 10)}…${value.slice(-10)}`;
};

const OPERATION_TYPE_LABELS = {
  1: "Transferência Pix",
  2: "Pagamento Pix",
  "": "Pix enviado",
};

const DetailRow = ({ label, value, mono = false, onCopy }) => {
  if (!value) return null;

  return (
    <div className="pix-transfer-final__row">
      <span className="pix-transfer-final__label">{label}</span>
      <span className="flex min-w-0 items-start gap-1.5">
        <span
          className={`pix-transfer-final__value ${mono ? "font-mono text-xs" : ""}`}
          title={value}
        >
          {mono ? formatAuthValue(value) : value}
        </span>
        {onCopy && (
          <button
            type="button"
            className="btn-outline-icon shrink-0"
            onClick={onCopy}
            aria-label={`Copiar ${label}`}
            title="Copiar"
          >
            <TbCopy className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
};

const DetailPanel = ({ title, items = [], authValue, onCopyAuth }) => {
  const visibleItems = items.filter((item) => item?.value);
  if (!visibleItems.length) return null;

  return (
    <div className="pix-transfer-final__panel">
      <h3 className="pix-transfer-final__panel-title">{title}</h3>
      <div className="space-y-1.5">
        {visibleItems.map((item, index) => {
          const isAuth = item.title?.toLowerCase() === "autenticação";
          return (
            <DetailRow
              key={`${item.title}-${index}`}
              label={item.title}
              value={item.value}
              mono={isAuth}
              onCopy={isAuth && authValue ? onCopyAuth : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export const PixTransferFinalStep = ({
  transactionStatus,
  transactionData,
  onNewTransfer,
  onDismiss,
  sourceAccountLabel,
  sourceAccountBalance,
  sourceAccountBalanceLoading,
  hideToolbar = false,
}) => {
  const history = useHistory();
  const { addToast } = useToast();
  const { downloadPdf, loadingDownload } = useReceiptDownload(transactionData);
  const { printRef, print } = useReceiptPrint();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const sections = transactionData?.details || [];

  const {
    origemSection,
    destinoSection,
    identificacaoSection,
    recipientName,
    recipientBank,
    recipientDocument,
    authId,
    endToEndId,
  } = useMemo(() => {
    const origem = sections.find((item) => item.value?.toLowerCase() === "origem");
    const destino = sections.find((item) => item.value?.toLowerCase() === "destino");
    const identificacao = sections.find((item) => {
      const key = item.value?.toLowerCase() || "";
      return key === "identificação" || key === "identificacao";
    });
    const recipient =
      findDetailValue(sections, "Destino", "Recebedor") ||
      findDetailValue(sections, "Destino", "Nome") ||
      transactionData?.receiver?.name;
    const bank =
      findDetailValue(sections, "Destino", "Banco") || transactionData?.receiver?.bank_name;
    const document =
      findDetailValue(sections, "Destino", "Documento do recebedor") ||
      findDetailValue(sections, "Destino", "Documento") ||
      transactionData?.receiver?.document;
    const auth =
      findDetailValue(sections, "Identificação", "Autenticação") ||
      findDetailValue(sections, "Identificacao", "Autenticação") ||
      findDetailValue(sections, "Origem", "Autenticação") ||
      findDetailValue(sections, "Destino", "Autenticação");
    const e2e =
      findDetailValue(sections, "Identificação", "ID da transação") ||
      findDetailValue(sections, "Identificacao", "ID da transação") ||
      auth;

    return {
      origemSection: origem,
      destinoSection: destino,
      identificacaoSection: identificacao,
      recipientName: recipient,
      recipientBank: bank,
      recipientDocument: document,
      authId: auth,
      endToEndId: e2e,
    };
  }, [sections, transactionData]);

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
      return;
    }
    history.push("/pix");
  }, [history, onDismiss]);

  const handleCopyAuth = useCallback(async () => {
    if (!authId) return;

    try {
      await navigator.clipboard.writeText(authId);
      addToast({
        title: "Copiado",
        description: "Código de autenticação copiado.",
        type: "success",
      });
    } catch {
      addToast({
        title: "Não foi possível copiar",
        description: "Copie manualmente o código de autenticação.",
        type: "error",
      });
    }
  }, [authId, addToast]);

  const handleShare = useCallback(async () => {
    const amountLabel = formatCurrency(transactionData?.amount);
    const dateLabel = transactionData?.date
      ? moment(transactionData.date).format("DD/MM/YYYY [às] HH:mm")
      : "";
    const shareText = [
      "Pix enviado com sucesso",
      `Valor: ${amountLabel}`,
      recipientName ? `Para: ${recipientName}` : null,
      dateLabel ? `Data: ${dateLabel}` : null,
      endToEndId ? `ID: ${endToEndId}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Comprovante Pix — Valut",
          text: shareText,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      addToast({
        title: "Copiado",
        description: "Resumo do comprovante copiado para a área de transferência.",
        type: "success",
      });
    } catch {
      addToast({
        title: "Não foi possível compartilhar",
        description: "Tente baixar o PDF ou imprimir o comprovante.",
        type: "error",
      });
    }
  }, [transactionData, recipientName, endToEndId, addToast]);

  if (!transactionStatus) {
    return (
      <div className="pix-transfer-final space-y-4">
        <section className="app-card p-5 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
            <TbX className="h-5 w-5 text-red-500" />
          </div>
          <h2 className="mt-3 text-base font-bold text-brand-dark">Transferência não concluída</h2>
          <p className="mt-1 text-sm text-gray-500">
            Não foi possível finalizar o Pix. Tente novamente ou volte para revisar os dados.
          </p>
          <button type="button" className="btn-outline mt-4" onClick={onNewTransfer}>
            <TbArrowLeft className="h-4 w-4" />
            Tentar novamente
          </button>
        </section>
      </div>
    );
  }

  const formattedLongDate = transactionData?.date
    ? moment(transactionData.date).format("dddd, D [de] MMMM [de] YYYY [às] HH:mm")
    : "—";

  const formattedShortDate = transactionData?.date
    ? moment(transactionData.date).format("DD/MM/YYYY [às] HH:mm")
    : "—";

  const amountLabel = formatCurrency(transactionData?.amount);
  const operationType =
    OPERATION_TYPE_LABELS[transactionData?.type ?? ""] || "Pix enviado";

  const recipientMeta = [recipientBank, recipientDocument].filter(Boolean).join(" · ");

  const identificacaoItems =
    identificacaoSection?.items ||
    [
      endToEndId && { title: "ID da transação", value: endToEndId },
      authId && { title: "Autenticação", value: authId },
    ].filter(Boolean);

  return (
    <div className="pix-transfer-final space-y-4">
      {!hideToolbar && (
        <div className="pix-transfer-final__toolbar">
          <button
            type="button"
            className="pix-transfer-final__back-link"
            onClick={handleDismiss}
          >
            <TbArrowLeft className="h-4 w-4 shrink-0" />
            Voltar para Área Pix
          </button>
          <button type="button" className="btn-primary shrink-0" onClick={onNewTransfer}>
            <TbArrowsLeftRight className="h-4 w-4" />
            Nova transferência
          </button>
        </div>
      )}

      <div ref={printRef} className="space-y-4">
        <section className="app-card pix-transfer-final__receipt overflow-hidden">
          <div className="pix-transfer-final__receipt-head">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <TbCheck className="h-6 w-6 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h2 className="mt-4 text-base font-bold text-brand-dark">Pix enviado com sucesso</h2>
            <p className="mt-1 text-sm capitalize text-gray-500">{formattedLongDate}</p>
            <p className="mt-4 text-3xl font-bold tracking-tight text-brand-dark">{amountLabel}</p>
            {recipientName && (
              <p className="mt-3 text-sm text-gray-600">
                Para: <strong className="font-semibold text-brand-dark">{recipientName}</strong>
              </p>
            )}
            {recipientMeta && (
              <p className="mt-1 text-xs text-gray-500">{recipientMeta}</p>
            )}
          </div>

          <div className="pix-transfer-final__balance-strip">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium text-emerald-800">Saldo após envio</span>
              <span className="text-[11px] text-emerald-700/80">{sourceAccountLabel || "Conta"}</span>
            </div>
            <span className="shrink-0 text-base font-bold text-emerald-900">
              {sourceAccountBalanceLoading ? (
                <CircularProgress size={16} sx={{ color: "#059669" }} />
              ) : (
                sourceAccountBalance || "—"
              )}
            </span>
          </div>
        </section>

        <div className="pix-transfer-final__actions">
          <button type="button" className="btn-primary" onClick={handleShare}>
            <TbShare2 className="h-4 w-4" />
            Compartilhar
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={downloadPdf}
            disabled={loadingDownload}
          >
            <TbDownload className="h-4 w-4" />
            {loadingDownload ? "Gerando PDF…" : "Baixar PDF"}
          </button>
          <button type="button" className="btn-outline" onClick={print}>
            <TbPrinter className="h-4 w-4" />
            Imprimir
          </button>
        </div>

        <div className="pix-transfer-final__summary-bar">
          <div className="pix-transfer-final__summary-col">
            <span className="pix-transfer-final__summary-label">Data e hora</span>
            <span className="pix-transfer-final__summary-value">{formattedShortDate}</span>
          </div>
          <div className="pix-transfer-final__summary-col">
            <span className="pix-transfer-final__summary-label">ID transação</span>
            <span
              className="pix-transfer-final__summary-value font-mono text-xs"
              title={endToEndId || authId || undefined}
            >
              {formatAuthValue(endToEndId || authId) || "—"}
            </span>
          </div>
          <div className="pix-transfer-final__summary-col">
            <span className="pix-transfer-final__summary-label">Tipo operação</span>
            <span className="pix-transfer-final__summary-value">{operationType}</span>
          </div>
        </div>

        <div className="pix-transfer-final__accordion app-card overflow-hidden">
          <button
            type="button"
            className="pix-transfer-final__accordion-trigger"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
          >
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold text-brand-dark">Detalhes</span>
              <span className="pix-transfer-final__badge">Concluído</span>
            </span>
            <TbChevronDown
              className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${detailsOpen ? "rotate-180" : ""}`}
            />
          </button>

          {detailsOpen && (
            <div className="pix-transfer-final__accordion-body">
              <div className="grid gap-4 md:grid-cols-2">
                <DetailPanel
                  title="Origem"
                  items={origemSection?.items}
                  authValue={authId}
                  onCopyAuth={handleCopyAuth}
                />
                <DetailPanel
                  title="Destino"
                  items={destinoSection?.items}
                  authValue={authId}
                  onCopyAuth={handleCopyAuth}
                />
              </div>
              {identificacaoItems.length > 0 && (
                <div className="pix-transfer-final__section">
                  <DetailPanel
                    title="Identificação"
                    items={identificacaoItems}
                    authValue={authId}
                    onCopyAuth={handleCopyAuth}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="pix-transfer-final__footer">
        Comprovante oficial · Transação realizada via Valut
        {transactionData?.supportPhone ? ` · ${transactionData.supportPhone}` : ""}
      </p>
    </div>
  );
};
